/**
 * useCrossPortal Hook for Ufficio Stile Portal
 * 
 * React hook for managing cross-portal operations including completion and notification
 * functionality for FASE 6 workflow.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { 
  crossPortalService, 
  CrossPortalCompletionRequest, 
  CrossPortalCompletionResponse 
} from '../services/api/cross-portal';

export interface UseCrossPortalOptions {
  onCompleteSuccess?: (response: CrossPortalCompletionResponse) => void;
  onCompleteError?: (error: Error) => void;
}

export function useCrossPortal(options: UseCrossPortalOptions = {}) {
  const queryClient = useQueryClient();

  // Mutation for completing and notifying
  const completeAndNotifyMutation = useMutation<
    CrossPortalCompletionResponse,
    Error,
    CrossPortalCompletionRequest
  >({
    mutationFn: (request) => crossPortalService.completeAndNotify(request),
    onSuccess: (response) => {
      // Show success notification
      notifications.show({
        title: 'Completamento Riuscito',
        message: `Richiesta completata con successo. Notificati: ${response.notifiedPortals.join(', ')}`,
        color: 'green',
        autoClose: 5000,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['cross-portal'] });

      options.onCompleteSuccess?.(response);
    },
    onError: (error) => {
      // Show error notification
      notifications.show({
        title: 'Errore Completamento',
        message: error.message || 'Errore durante il completamento della richiesta',
        color: 'red',
        autoClose: 7000,
      });

      options.onCompleteError?.(error);
    },
  });

  // Query for assigned cross-portal requests
  // const assignedRequestsQuery = useQuery({
  //   queryKey: ['cross-portal', 'assigned-requests'],
  //   queryFn: () => crossPortalService.getAssignedRequests(),
  //   staleTime: 5 * 60 * 1000, // 5 minutes
  //   refetchInterval: 30 * 1000, // 30 seconds
  // });

  // Function to get request status
  const getRequestStatus = (requestId: string) => {
    return useQuery({
      queryKey: ['cross-portal', 'status', requestId],
      queryFn: () => crossPortalService.getRequestStatus(requestId),
      enabled: !!requestId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };

  return {
    // Mutations
    completeAndNotify: completeAndNotifyMutation.mutateAsync,
    completeAndNotifyMutation,

    // Queries
    // assignedRequests: assignedRequestsQuery.data || [],
    // assignedRequestsLoading: assignedRequestsQuery.isLoading,
    // assignedRequestsError: assignedRequestsQuery.error,

    // Utilities
    getRequestStatus,
    
    // Loading states
    isCompleting: completeAndNotifyMutation.isPending,
  };
}

export default useCrossPortal;
